import _ from "lodash";
import { checkIfUpdateSetsSlideRefsToNull } from "js/core/storage/utils/apiAdapterUtils";
import type Logger from "js/core/logger/Logger";

export type SlideRefs = Record<string, number>;

// Converts an ordered list of slide ids into the persisted slideRefs map shape
export function slideOrderToRefs(slideIds: string[]): SlideRefs {
    return slideIds.reduce<SlideRefs>((slideRefs, slideId, index) => {
        slideRefs[slideId] = index;
        return slideRefs;
    }, {});
}

// Returns the slide ids in the order they should appear after a move
export function computeMovedSlideOrder(currentSlideIds: string[], movedSlideIds: string[], index: number): string[] {
    const remainingSlideIds = currentSlideIds.filter(id => !movedSlideIds.includes(id));
    const insertIndex = Math.max(0, Math.min(index, remainingSlideIds.length));

    remainingSlideIds.splice(insertIndex, 0, ...movedSlideIds);

    return remainingSlideIds;
}

export function troubleshootingLogs({ id, slideIds, index, sips, slideRefs, updatedSlideRefs, changeSet, logger }: {
    id: string;
    slideIds: string[];
    index: number;
    sips: string[];
    slideRefs: SlideRefs | undefined;
    updatedSlideRefs: SlideRefs;
    changeSet: any;
    logger: Logger;
}): void {
    const loggerPayload = {
        id,
        slideIds,
        index,
        sips,
        slideRefs,
        updatedSlideRefs
    };
    logger.info(`[presentation] moveSlides() id: ${id}`, loggerPayload);

    if (Object.keys(updatedSlideRefs).length < Object.keys(slideRefs || {}).length) {
        const err = new Error("[presentation] moveSlides() changed the number of slides in the presentation, please investigate");
        logger.error(err, err.message, loggerPayload);
    }

    try {
        // Calculate new slideRefs using a pure function, trying to make things more functional and testable very carefully
        const updatedSlideRefs2 = slideOrderToRefs(computeMovedSlideOrder(sips, slideIds, index));

        if (!_.isEqual(updatedSlideRefs, updatedSlideRefs2)) {
            const err = new Error("[presentation] moveSlides() mismatch between legacy slideRef calculation and alternative version");
            logger.error(err, err.message, { ...loggerPayload, updatedSlideRefs2 });
        }
    } catch (err) {
        logger.error(err, "[presentation] moveSlides() experimental updatedSlideRefs2 threw an error during calculation",
            loggerPayload);
    }
    try {
        if (checkIfUpdateSetsSlideRefsToNull(changeSet.update)) {
            const err = new Error("[presentation] moveSlides() set slideRefs to null, please investigate");
            logger.error(err, err.message, { changeSet, sips, slideRefs, updatedSlideRefs, id });
        }
    } catch (err) {
        logger.error(err, "[presentation] moveSlides() checkIfUpdateSetsSlideRefsToNull() threw an error",
            loggerPayload);
    }
}
